import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Shield, Users, Leaf, Eye, Scale, Sparkles, BookOpen, Compass } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const IMG_PHILOSOPHY = "https://images.unsplash.com/photo-1758274526671-ad18176acb01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx3ZWxsbmVzcyUyMG5hdHVyZSUyMG1lZGl0YXRpb258ZW58MHx8fHwxNzc2MDc3MTYyfDA&ixlib=rb-4.1.0&q=85";

const coreValues = [
  { icon: Heart, title: "Compassion (Karuna)", desc: "Every initiative begins with empathy" },
  { icon: Sparkles, title: "Awareness (Chetna)", desc: "Conscious and mindful action" },
  { icon: Shield, title: "Integrity (Satya)", desc: "Transparency and accountability" },
  { icon: Scale, title: "Right Action (Samyak Karma)", desc: "Ethical and meaningful work" },
  { icon: Users, title: "Inclusivity (Samta)", desc: "Equal respect for all" },
  { icon: Leaf, title: "Sustainability (Dharma Anugati)", desc: "Long-term impact focus" },
];

const approaches = [
  { title: "Community-First Execution", desc: "Understanding real needs at the grassroots level" },
  { title: "Integrated Model", desc: "Addressing multiple aspects of life simultaneously" },
  { title: "Partnership-Driven Growth", desc: "Collaborating with institutions, experts, and communities" },
  { title: "Sustainable Impact", desc: "Focusing on long-term transformation rather than short-term relief" },
];

const missions = [
  "Promote meditation and mindfulness for mental well-being",
  "Enable access to government welfare schemes and social support",
  "Improve healthcare accessibility at grassroots levels",
  "Build digital awareness and technical capabilities",
  "Foster sustainable and community-driven development",
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* SECTION 1 — OPENING STATEMENT */}
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">
              About BSM Welfare Foundation
            </h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-4xl leading-relaxed">
              BSM Welfare Foundation (Bhavatu Sabba Mangalam) is a purpose-driven non-profit organization committed to creating a balance between inner peace and societal progress. Rooted in the timeless blessing "May all beings be happy," the foundation integrates spiritual awareness with practical social initiatives to build a more conscious, compassionate, and empowered society.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 2 — WHO WE ARE */}
      <section data-testid="who-we-are" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl">
            <p className="overline mb-3">Our Identity</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">Who We Are</h2>
            <div className="space-y-5 text-[#7A827E] text-base leading-relaxed">
              <p>BSM Welfare Foundation is a Section 8 registered organization established with a vision to serve humanity through a holistic model of development.</p>
              <p>In a rapidly evolving world where material growth often overshadows mental well-being, we aim to bridge the gap between inner awareness and external progress.</p>
              <p>Unlike conventional organizations that operate in isolated sectors, BSM Foundation follows an integrated approach — addressing mental health, social security, healthcare access, and digital empowerment together.</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 3 — OUR PHILOSOPHY */}
      <section data-testid="philosophy" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="overline mb-3">Guiding Principle</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">Our Philosophy</h2>
              <p className="text-[#7A827E] text-base leading-relaxed mb-5">
                True progress is incomplete without inner balance.
              </p>
              <p className="text-[#7A827E] text-base leading-relaxed mb-5">
                While modern society continues to advance technologically and economically, stress, disconnection, and lack of awareness remain widespread challenges. BSM Welfare Foundation believes that lasting change can only be achieved when awareness (Chetna), compassion (Karuna), and right action (Samyak Karma) come together.
              </p>
              <div className="bg-white rounded-2xl p-6 border border-[#E2E5E3]/50 shadow-sm">
                <p className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-1">Our approach is simple yet powerful:</p>
                <p className="text-[#E28743] font-medium">Strengthen the mind &rarr; Empower the individual &rarr; Transform the community</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#8ECAE6]/10 rounded-3xl -rotate-3" />
                <img src={IMG_PHILOSOPHY} alt="Meditation and mindfulness" className="relative rounded-2xl w-full h-[420px] object-cover shadow-[0_20px_40px_rgba(0,0,0,0.08)]" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SECTION 4 — VISION */}
      <section data-testid="vision" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <p className="overline mb-3">Looking Ahead</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">Our Vision</h2>
            <p className="text-[#7A827E] text-lg leading-relaxed">
              To nurture a world where peace of mind and progress of society walk hand in hand — ensuring dignity, happiness, and well-being for all, without discrimination or exclusion.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 5 — MISSION */}
      <section data-testid="mission" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl mx-auto text-center mb-12">
            <p className="overline mb-3">Our Purpose</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-4">Our Mission</h2>
            <p className="text-[#7A827E]">We work with a structured mission across multiple dimensions:</p>
          </AnimatedSection>
          <div className="max-w-2xl mx-auto space-y-4">
            {missions.map((m, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.04)]" data-testid={`mission-item-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-[#E28743]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Compass size={16} className="text-[#E28743]" strokeWidth={1.5} />
                  </div>
                  <p className="text-[#2C3531] text-sm font-medium">{m}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — CORE VALUES */}
      <section data-testid="core-values" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-14">
            <p className="overline mb-3">Our Foundation</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E]">Our Core Values</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValues.map((val, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <Card data-testid={`value-card-${i}`} className="bg-white border-0 rounded-3xl p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 h-full">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 rounded-2xl bg-[#E28743]/10 flex items-center justify-center mb-4">
                      <val.icon size={22} className="text-[#E28743]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-1.5">{val.title}</h3>
                    <p className="text-[#7A827E] text-sm">{val.desc}</p>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — OUR APPROACH */}
      <section data-testid="our-approach" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-14">
            <p className="overline mb-3">How We Work</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-4">Our Approach</h2>
            <p className="text-[#7A827E] max-w-2xl mx-auto">Our work is designed to be grounded, structured, and scalable.</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {approaches.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div data-testid={`approach-card-${i}`} className="flex gap-5 p-7 rounded-3xl bg-white border border-[#E2E5E3]/50 shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1">
                  <div className="w-10 h-10 rounded-xl bg-[#606C38]/10 flex items-center justify-center shrink-0">
                    <BookOpen size={20} className="text-[#606C38]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-base font-semibold text-[#1B201E] mb-1">{item.title}</h3>
                    <p className="text-[#7A827E] text-sm">{item.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — GOVERNANCE & STRUCTURE */}
      <section data-testid="governance-structure" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <p className="overline mb-3">Our Framework</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">Governance & Structure</h2>
            <p className="text-[#7A827E] text-base leading-relaxed mb-5">
              BSM Welfare Foundation is registered as a Section 8 Company under the Companies Act, ensuring a professional governance framework with a strong emphasis on transparency and accountability.
            </p>
            <p className="text-[#7A827E] text-base leading-relaxed">
              The organization is guided by a group of socially committed individuals and spiritual mentors, combining professional expertise with a deep sense of service.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 9 — OUR JOURNEY */}
      <section data-testid="our-journey" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <p className="overline mb-3">The Beginning</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-6">Our Journey</h2>
            <p className="text-[#7A827E] text-base leading-relaxed mb-5">
              BSM Welfare Foundation is at the beginning of its journey — built on strong intent, clear structure, and a long-term vision.
            </p>
            <p className="text-[#7A827E] text-base leading-relaxed">
              Starting from grassroots initiatives, the foundation aims to expand its reach gradually across regions, creating measurable and meaningful impact over time.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 10 — CLOSING STATEMENT */}
      <section className="py-20 sm:py-28 bg-[#1B201E]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-5">
              A Movement Towards Conscious Living
            </h2>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto mb-5">
              BSM Welfare Foundation is more than an organization — it is a movement that brings together awareness, compassion, and action.
            </p>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto">
              Our goal is not just to support communities, but to inspire individuals to live with clarity, balance, and purpose.
            </p>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
